import React, { memo, useCallback, useRef, useState } from 'react';
import { type ScrollView, StyleSheet, View } from 'react-native';

import { type DietaryProperty, IngredientKind, ModifierGroupVariation } from '@sg/graphql-schema';
import {
  type CustomizationMachineTypes,
  CustomizationReview,
} from '@sg/shared-fe/features/customization';

import { CustomizationSelectedIngredients } from '@order/features/customization';
import { useFeatureFlag } from '@order/LaunchDarkly';

import { useDietaryRestrictions } from '../../../../../../../../components';
import { useModificationGroupHelperText } from '../../../../hooks';

// ─────────────────────────────────────────────────────────────────────────────

export const CustomizationIngredientsReviewPanel = memo((props: Props) => {
  const {
    product,
    activeModifications,
    modifierGroups,
    modificationLimits,
    addIngredientModification,
    removeIngredientModification,
    completelyRemoveIngredientModification,
    getFormattedQuantity,
    onAddMoreIngredients,
    checkIfCanAddMore,
  } = props;

  const getSectionHelperText = useModificationGroupHelperText();
  const getRestrictionNotice = useDietaryRestrictions({ referrer: 'Customization' }); // prettier-ignore
  const isModificationGroupHelperTextEnabled = useFeatureFlag('CELS-4762-modification-group-helper-text-enabled'); // prettier-ignore

  // ─── Refs ────────────────────────────────────────────────────────────

  const detailsContainerRef = useRef<ScrollView>(null);

  // ─── State ───────────────────────────────────────────────────────────

  const [activeIngredientId, setActiveIngredientId] = useState<string>();

  const clearActiveIngredientId = useCallback(() => {
    setActiveIngredientId(undefined);
  }, []);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <View style={styles.reviewPanelContainer}>
      <CustomizationSelectedIngredients.Container>
        <CustomizationSelectedIngredients.List>
          {modifierGroups?.map((group, index) => {
            const isLastSection = index === product.modifierGroups.length - 1;

            const ingredientsForThisSection = activeModifications.filter(
              (modification) => modification.modificationGroupId === group.id,
            );

            const customHelperText = getSectionHelperText({
              freeAggregateQuantity: modificationLimits[group.name]?.freeAggregateQuantity,
              ingredientKind: group.modificationIngredientKind,
            });
            const helperText = isModificationGroupHelperTextEnabled
              ? group.helperText
              : customHelperText;

            return (
              <CustomizationSelectedIngredients.IngredientKindSection
                key={group.id}
                ingredientKindName={group.displayName}
                helperText={helperText}
                style={isLastSection ? styles.withoutBorder : null}
                formattedQuantity={getFormattedQuantity(group.name)}
              >
                {ingredientsForThisSection.map((modification) => {
                  const isActive = activeIngredientId === modification.ingredient?.id;

                  const isDressing = modification.ingredient?.kind === IngredientKind.Dressing;

                  const maxPortionsForGroup = modificationLimits[group.name]?.maxAggregateQuantity ?? Number.POSITIVE_INFINITY; // prettier-ignore
                  const isSplitPortion = modification.modificationGroupVariation === ModifierGroupVariation.SplitPortion; // prettier-ignore
                  const isHalfPortion = isSplitPortion && modification.quantity < maxPortionsForGroup; // prettier-ignore

                  const restrictionNotice = getRestrictionNotice(
                    modification?.ingredient?.dietaryProperties,
                  );
                  const dietaryRestrictions = modification?.ingredient?.dietaryProperties
                    .map(prepareDietaryRestrictions)
                    .join(', ');

                  if (!modification.ingredient) {
                    return null;
                  }

                  return (
                    <CustomizationSelectedIngredients.ListItem
                      key={modification.ingredient.id}
                      isActive={isActive}
                      shouldScrollToSelf={false}
                      scrollViewRef={detailsContainerRef}
                    >
                      <CustomizationSelectedIngredients.IngredientV2
                        ingredientId={modification.ingredient.id}
                        ingredientName={modification.ingredient.name}
                        ingredientAsset={modification.ingredient.asset}
                        quantity={modification.quantity}
                        isActive={isActive}
                        isDisabled={isDressing}
                        restrictionNotice={restrictionNotice}
                        dietaryRestrictions={dietaryRestrictions}
                        isSplitPortion={isSplitPortion}
                        isHalfPortion={isHalfPortion}
                        setActive={setActiveIngredientId}
                        setInactive={clearActiveIngredientId}
                        onIncreaseQuantity={addIngredientModification}
                        onDecreaseQuantity={removeIngredientModification}
                        onRemove={completelyRemoveIngredientModification}
                      />
                    </CustomizationSelectedIngredients.ListItem>
                  );
                })}

                <CustomizationReview.AddMoreIngredientsButton
                  modificationGroupId={group.id}
                  modificationGroupName={group.displayName}
                  onPress={onAddMoreIngredients}
                  isDisabled={!checkIfCanAddMore(group.name)}
                />
              </CustomizationSelectedIngredients.IngredientKindSection>
            );
          })}
        </CustomizationSelectedIngredients.List>
      </CustomizationSelectedIngredients.Container>
    </View>
  );
});

// ─── Helpers ─────────────────────────────────────────────────────────────────

function prepareDietaryRestrictions(dietaryProperty: DietaryProperty) {
  return dietaryProperty.name.toLowerCase();
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  reviewPanelContainer: {
    flex: 1,
  },
  withoutBorder: {
    borderBottomWidth: 0,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  product: CustomizationMachineTypes.Product;
  activeModifications: CustomizationMachineTypes.Modification[];
  modifierGroups: CustomizationMachineTypes.ExtendedModifierGroup[] | undefined;
  modificationLimits: CustomizationMachineTypes.ModificationGroupLimits;
  addIngredientModification: (ingredientId: string) => void;
  removeIngredientModification: (ingredientId: string) => void;
  completelyRemoveIngredientModification: (ingredientId: string) => void;
  getFormattedQuantity: (groupName: string) => string | undefined;
  onAddMoreIngredients: (groupId: string, groupName: string) => void;
  checkIfCanAddMore: (groupId: string) => boolean;
};
